import React, {useEffect, useState} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import Api from "../util/Api";
import Promise from "bluebird";
import m from "moment";
import _ from "lodash";
import MiniLoader from "./MiniLoader";
import ClockDisplayGroup from "./ClockDisplayGroup";
import ClockDisplay from "./ClockDisplay";
import MovementScore from "../pages/facilities/sections/rooms/show/partial/MovementScore";
import {sleepTooltip, respirationTooltip, outOfRangeTooltip} from "./Tooltips/WeeklyChartTooltips";
import {WhisperWrapper} from "./Tooltips/WhisperWrapper";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);
export const sleepOptions = {
    indexAxis: 'y',
    plugins: {
        tooltip: {
            enabled: true,
            callbacks: {
                label: (context) => {
                    let label = context.dataset.label || "";
                    const dayTimeInMinutes = context.raw / 1000 / 60;
                    const dayHours = Math.abs(Math.floor(dayTimeInMinutes / 60));
                    const dayMinutes = Math.abs(Math.round(dayTimeInMinutes % 60));

                    if (!dayHours && !dayMinutes) {
                        return "";
                    }
                    if (label) {
                        label += ": ";
                    }
                    if (dayHours) {
                        label += `${dayHours}h `;
                    }
                    if (dayMinutes) {
                        label += `${dayMinutes}m`;
                    }
                    return label;
                }
            }
        },
        title: {
            display: false,
            text: 'Chart.js Bar Chart - Stacked',
        },
    },
    responsive: true,
    scales: {
        x: {
            ticks: {
                beginAtZero: true,
                fontFamily: "'Open Sans Bold', sans-serif",
                fontSize: 11,
                callback: (value) => {
                    const dayTimeInMinutes = value / 1000 / 60;
                    const dayHours = Math.abs(Math.floor(dayTimeInMinutes / 60));
                    return `${dayHours}`;
                }
            },
            stacked: true,
        },
        y: {
            gridLines: {
                display: false,
                color: "#fff",
                zeroLineColor: "#fff",
                zeroLineWidth: 0
            },
            ticks: {
                fontFamily: "'Open Sans Bold', sans-serif",
                fontSize: 11
            },
            stacked: true,
        },
    },
};

function WeeklyCharts(props) {
    const [sleepLoading, setSleepLoading] = useState(false);
    const [sleepChart, setSleepChart] = useState([]);
    const [respirationChart, setRespirationChart] = useState([]);
    const [outOfRangeChart, setOutOfRangeChart] = useState([]);
    const [movementScoreChart, setMovementScoreChart] = useState([]);
    const [endTime, setEndTime] = useState(m().add(1, "days").hours(0)
        .minutes(0)
        .seconds(0));
    const [startTime, setStartTime] = useState(endTime.clone().subtract(7, "days"));
    const [patientId, setPatientId] = useState(props.patientId);
    const [tabSelected, setTabSelected] = useState(1);
    const [sleepActive, setSleepActive] = useState(true);
    const [respirationActive, setRespirationActive] = useState(false);
    const [outOfRangeActive, setOutOfRangeActive] = useState(false);
    const [movementActive, setMovementActive] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(true);

    const patientHighRR = props.patientSettings.normal_high;
    const patientLowRR = props.patientSettings.normal_low;
    const currentMovementScore = props.currentMovementScore;

    useEffect(() => {
        loadSleep();
        loadRespiration();
        loadOutOfRange();
        loadMovementScore();
    }, [patientHighRR, patientLowRR, currentMovementScore]);

    function loadSleep() {
        setSleepLoading(true);
        return Api.withAuth(props.auth)
            .getPatientSleep({
                id: patientId,
                startTimestamp: startTime.valueOf(),
                endTimestamp: endTime.valueOf()
            })
            .then((data) => Promise.fromCallback((callback) => {
                setSleepLoading(false);
                setSleepChart(data);
            }))
            .catch((e) => {
                setSleepLoading(false);
                console.log(e);
                setSleepChart([]);
            });
    }

    function loadRespiration() {
        if (!patientHighRR) return;
        return Api.withAuth(props.auth)
            .getPatientRespiration({
                id: patientId,
                startTimestamp: startTime.valueOf(),
                endTimestamp: endTime.valueOf(),
                patientHighRR: patientHighRR,
                patientLowRR: patientLowRR
            })
            .then((data) => Promise.fromCallback((callback) => {
                setRespirationChart(data);
            }))
            .catch((e) => {
                console.log(e);
                setRespirationChart([]);
            });
    }

    function loadOutOfRange() {
        return Api.withAuth(props.auth)
            .getPatientOutOfRange({
                id: patientId,
                startTimestamp: startTime.valueOf(),
                endTimestamp: endTime.valueOf(),
            })
            .then((data) => Promise.fromCallback((callback) => {
                setOutOfRangeChart(data);
            }))
            .catch((e) => {
                console.log(e);
                setOutOfRangeChart([]);
            });
    }

    function loadMovementScore() {
        return Api.withAuth(props.auth)
            .getPatientMovementScore({
                id: patientId,
                startTimestamp: startTime.valueOf(),
                endTimestamp: endTime.valueOf(),
            })
            .then((data) => Promise.fromCallback((callback) => {
                // If we are looking at the current week and there is a current movement score then set today's movement score
                // in graph to the current movement score calculated elsewhere
                if (data && currentWeek) {
                    data[data.length - 1].score = currentMovementScore ? parseFloat(currentMovementScore) : 0;
                }
                setMovementScoreChart(data);
            }))
            .catch((e) => {
                console.log(e);
                setMovementScoreChart([]);
            });
    }

    function onTabSelected(tabNumber) {
        setTabSelected(tabNumber);
        switch (tabNumber) {
            case 1:
                setSleepActive(true);
                setRespirationActive(false);
                setOutOfRangeActive(false);
                setMovementActive(false);
                break;

            case 2:
                setSleepActive(false);
                setRespirationActive(true);
                setOutOfRangeActive(false);
                setMovementActive(false);
                break;

            case 3:
                setSleepActive(false);
                setRespirationActive(false);
                setOutOfRangeActive(true);
                setMovementActive(false);
                break;

            case 4:
                setSleepActive(false);
                setRespirationActive(false);
                setOutOfRangeActive(false);
                setMovementActive(true);
                break;
        }
    }

    const decrementSleepRange = () => {
        setStartTime(startTime.subtract(7, "days"));
        setEndTime(endTime.subtract(7, "days"));
        setCurrentWeek(endTime.format('MMMM D') === m().format('MMMM D'));
        try {
            loadSleep();
            loadRespiration();
            loadOutOfRange();
            loadMovementScore();
        } catch (e) {
            console.log(e);
        }
    };
    const incrementSleepRange = () => {
        setEndTime(endTime.add(7, "days"));
        setStartTime(startTime.add(7, "days"));
        if (endTime.format('MMMM D') === m().format('MMMM D')) {
            setCurrentWeek(true);
            return false;
        }
        setCurrentWeek(false);
        loadSleep();
        loadRespiration();
        loadOutOfRange();
        loadMovementScore();
    };
    const calculateAverageSleepDayNight = (sleepDays) => {
        const totalSleepTimeMillis = _.reduce(
            sleepDays,
            (result, value, key) => result + value.nightHours + value.dayHours,
            0
        );

        const daysToTrack = _.reduce(
            sleepDays,
            (result, value) => result += value.nightHours > 1800000 || value.dayHours > 1800000 ? 1 : 0,
            0
        );
        return daysToTrack > 0 ? totalSleepTimeMillis / 1000 / 60 / (daysToTrack) : 0;
    };
    const calculateAverageNormalRespirationDay = (respirationDays) => {
        const totalNormalRespirationTimeMillis = _.reduce(
            respirationDays,
            (result, value, key) => result + value.normalHours,
            0
        );
        const daysToTrack = _.reduce(
            respirationDays,
            (result, value) => result += value.normalHours > 1800000 ? 1 : 0,
            0
        );

        return daysToTrack > 0 ? totalNormalRespirationTimeMillis / 1000 / 60 / (daysToTrack) : 0;
    };
    const calculateAverageHighRespirationDay = (respirationDays) => {
        const totalHighRespirationTimeMillis = _.reduce(
            respirationDays,
            (result, value, key) => result + value.highHours,
            0
        );

        const daysToTrack = _.reduce(
            respirationDays,
            (result, value) => result += value.highHours > 1800000 ? 1 : 0,
            0
        );

        return daysToTrack > 0 ? totalHighRespirationTimeMillis / 1000 / 60 / (daysToTrack) : 0;
    };
    const calculateAverageLowRespirationDay = (respirationDays) => {
        const totalLowRespirationTimeMillis = _.reduce(
            respirationDays,
            (result, value, key) => result + value.lowHours,
            0
        );

        const daysToTrack = _.reduce(
            respirationDays,
            (result, value) => result += value.lowHours > 1800000 ? 1 : 0,
            0
        );

        return daysToTrack > 0 ? totalLowRespirationTimeMillis / 1000 / 60 / (daysToTrack) : 0;
    };
    const calculateAverageOutOfRangeDay = (outOfRangeDays) => {
        const totalOutOfRangeTimeMillis = _.reduce(
            outOfRangeDays,
            (result, value, key) => result + value.dayHours,
            0
        );

        const daysToTrack = _.reduce(
            outOfRangeDays,
            (result, value) => result += value.dayHours > 1800000 ? 1 : 0,
            0
        );
        return daysToTrack > 0 ? totalOutOfRangeTimeMillis / 1000 / 60 / (daysToTrack) : 0;
    };

    const totalSleepMinutes = calculateAverageSleepDayNight(sleepChart);
    const totalOutOfRangeMinutes = calculateAverageOutOfRangeDay(outOfRangeChart);
    const totalNormalRespirationMinutes = calculateAverageNormalRespirationDay(respirationChart);
    const totalHighRespirationMinutes = calculateAverageHighRespirationDay(respirationChart);
    const totalLowRespirationMinutes = calculateAverageLowRespirationDay(respirationChart);

    const sleepGraphData = {
        labels: _.map(sleepChart, (label) => {
            const formattedDay = m(label.timestamp).format('MM/DD');
            return `${label.name} ${formattedDay}`;
        }),

        datasets: [
            {
                label: "Night",
                data: _.map(sleepChart, ({nightHours}) => nightHours),
                backgroundColor: "#9127EE",
                hoverBackgroundColor: "#9127EE"
            },
            {
                label: "Day",
                data: _.map(sleepChart, ({dayHours}) => dayHours),
                backgroundColor: "#8CC841",
                hoverBackgroundColor: "#8CC841"
            }
        ]
    };

    const respirationGraphData = {
        labels: _.map(respirationChart, (label) => {
            const formattedDay = m(label.timestamp).format('MM/DD');
            return `${label.name} ${formattedDay}`;
        }),

        datasets: [
            {
                label: 'Normal',
                data: _.map(respirationChart, ({normalHours}) => normalHours),
                backgroundColor: "#989999",
                hoverBackgroundColor: "#989999"
            },
            {
                label: `Below ${patientLowRR} RR`,
                data: _.map(respirationChart, ({lowHours}) => lowHours),
                backgroundColor: "#ea781b",
                hoverBackgroundColor: "#ea781b"
            },
            {
                label: `Above ${patientHighRR} RR`,
                data: _.map(respirationChart, ({highHours}) => highHours),
                backgroundColor: "#bf2222",
                hoverBackgroundColor: "#bf2222"
            }
        ]
    };

    const outOfRangeGraphData = {
        labels: _.map(outOfRangeChart, (label) => {
            const formattedDay = m(label.timestamp).format('MM/DD');
            return `${label.name} ${formattedDay}`;
        }),

        datasets: [
            {
                label: "Time Out Of Range",
                data: _.map(outOfRangeChart, ({dayHours}) => dayHours),
                backgroundColor: "#22A3BF",
                hoverBackgroundColor: "#22A3BF"
            }
        ]
    };

    const scoreColor = (color) => {
        if (color.raw < 4.0) {
            return "rgb(247, 63, 63)";
        }

        if (color.raw >= 4.0 && color.raw <= 7.0) {
            return "rgba(233, 206, 33, 1)";
        }

        if (color.raw > 7.0) {
            return 'green';
        }

        return "#22A3BF";
    }

    const movementScoreGraphData = {
        labels: _.map(movementScoreChart, (label) => {
            const formattedDay = m(label.timestamp).format('MM/DD');
            return `${label.name} ${formattedDay}`;
        }),

        datasets: [
            {
                label: "Movement Score",
                data: _.map(movementScoreChart, ({score}) => score),
                backgroundColor: color => scoreColor(color),
                hoverBackgroundColor: color => scoreColor(color)
            }
        ]
    };
    const flexLeftTitle = {
        flexGrow: 4
    };
    const flexItem = {
        justifyContent: ''
    };
    return (
        <div style={{minHeight: '400px'}}>
            {sleepLoading ? (<MiniLoader size="2x"/>) : (
                <>
                    <div className='sleep-card-date-picker'>
                        <div style={flexLeftTitle}>
                            {startTime.format('MMM DD')} - {endTime.clone().subtract("1", "day").format('MMM DD YYYY')}
                        </div>
                        {/*{ sleepChart.length > 0 && sleepChart[0].dayStart && sleepChart[0].dayStart.substring(4, 11) } - { sleepChart[sleepChart.length - 1].dayStart.substring(4, 16) }*/}
                        <div className='sleep-card-nav'>
                            <button className='sleep-card-nav-btn sleep-card-nav-left'
                                    onClick={decrementSleepRange}></button>
                            <button className='sleep-card-nav-btn sleep-card-nav-right'
                                    onClick={incrementSleepRange}></button>
                        </div>
                    </div>
                    <hr/>
                    <div className="sleep-chart-category-nav">
                        <WhisperWrapper tooltip={sleepTooltip}
                                        props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}>
                            <button style={{
                                borderColor: '#22A3BF',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                backgroundColor: sleepActive ? '#22A3BF' : '#FFF',
                                borderRadius: '4px 0px 0px 4px',
                                fontWeight: '700',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                textTransform: 'capitalize',
                                color: sleepActive ? '#FFF' : '#22A3BF'
                            }}
                                    onClick={() => onTabSelected(1)}>Sleep
                            </button>
                        </WhisperWrapper>
                        <WhisperWrapper tooltip={respirationTooltip}
                                        props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}>
                            <button onClick={() => onTabSelected(2)} style={{
                                borderColor: '#22A3BF',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                backgroundColor: respirationActive ? '#22A3BF' : '#FFF',
                                fontWeight: '700',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                textTransform: 'capitalize',
                                color: respirationActive ? '#FFF' : '#22A3BF'
                            }}>Respiration
                            </button>
                        </WhisperWrapper>
                        <WhisperWrapper
                            tooltip={outOfRangeTooltip}
                            props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}>
                            <button onClick={() => onTabSelected(3)} style={{
                                borderColor: '#22A3BF',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                backgroundColor: outOfRangeActive ? '#22A3BF' : '#FFF',
                                fontWeight: '700',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                textTransform: 'capitalize',
                                color: outOfRangeActive ? '#FFF' : '#22A3BF'
                            }}>Out of Range
                            </button>
                        </WhisperWrapper>
                        <button onClick={() => onTabSelected(4)} style={{
                            borderColor: '#22A3BF',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            backgroundColor: movementActive ? '#22A3BF' : '#FFF',
                            borderRadius: '0px 4px 4px 0px',
                            fontWeight: '700',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            textTransform: 'capitalize',
                            color: movementActive ? '#FFF' : '#22A3BF'
                        }}>Movement
                        </button>
                    </div>
                    <hr/>
                    {tabSelected === 1 &&
                        <>
                            <div className='sleep-chart'>
                                <Bar height={'370'} width={'367'} options={sleepOptions} data={sleepGraphData}/>
                            </div>
                            <hr/>
                            {<ClockDisplay
                                label="Estimated Average"
                                timeInMinutes={totalSleepMinutes}
                            />}
                        </>
                    }
                    {tabSelected === 2 &&
                        <>
                            <div className='sleep-chart'>
                                <Bar height={'370'} width={'367'} options={sleepOptions} data={respirationGraphData}/>
                            </div>
                            <hr/>
                            {<ClockDisplayGroup
                                label="Estimated Normal Average"
                                timeInMinutes={totalNormalRespirationMinutes}
                            />}
                            <div style={{display: 'flex'}}>
                                {<ClockDisplayGroup
                                    label="Estimated Low Average"
                                    timeInMinutes={totalLowRespirationMinutes}
                                />}
                                {<ClockDisplayGroup
                                    label="Estimated High Average"
                                    timeInMinutes={totalHighRespirationMinutes}
                                />}
                            </div>
                        </>
                    }
                    {tabSelected === 3 &&
                        <>
                            <div className='sleep-chart'>
                                <Bar height={'370'} width={'367'} options={sleepOptions} data={outOfRangeGraphData}/>
                            </div>
                            <hr/>
                            {<ClockDisplay
                                label="Estimated Average"
                                timeInMinutes={totalOutOfRangeMinutes}
                            />}
                        </>
                    }
                    {tabSelected === 4 &&
                        <MovementScore graphData={movementScoreGraphData}/>
                        // <>
                        //     <div className='sleep-chart'>
                        //         <Bar height={'370'} width={'367'} options={sleepOptions} data={movementScoreGraphData}/>
                        //     </div>
                        //     <hr/>
                        //     {<ClockDisplay
                        //       label="Estimated Average"
                        //       timeInMinutes={totalOutOfRangeMinutes}
                        //     />}
                        // </>
                    }
                </>)
            }

        </div>
    );
}

export default WeeklyCharts;
